import { makeStyles, Typography } from "@material-ui/core"
import clsx from "clsx"
import PasswordValidator from "password-validator"
import React, { FC, useEffect, useState } from "react"
import { colors } from "../../utils/theme"
import CheckIcon from "../atoms/CheckIcon"

const useStyles = makeStyles((theme) => ({
  container: {
    position: "absolute",
    zIndex: 1000,
    top: -76,
    left: 330,
  },
  passwordRules: {
    width: 252,
    padding: 12,
    borderRadius: 4,
    backgroundColor: colors.neutral.space,
  },
  checkContainer: {
    display: "flex",
    alignItems: "center",
  },
  check: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
  },
  checkSpacer: {
    height: 16,
    width: 16,
  },
  title: {
    fontWeight: 700,
    color: colors.neutral.white,
  },
  rule: {
    fontWeight: 400,
    color: colors.neutral.white,
    margin: 0,
    padding: 0,
  },
  marginBottom12: {
    marginBottom: 12,
  },
  triangle: {
    width: 0,
    height: 0,
    borderStyle: "solid",
    borderWidth: "8px 8px 8px 0",
    borderColor: `transparent ${colors.neutral.space} transparent transparent`,
    position: "absolute",
    zIndex: 1000,
    top: "50%",
    left: -8,
  },
}))

interface Props {
  password: string
  comparisonPassword: string
  setValidPassword: (value: boolean) => void
  left?: number
  compare?: boolean
}

const PasswordRules: FC<Props> = ({
  password,
  comparisonPassword,
  setValidPassword,
  left,
  compare = false,
}) => {
  const classes = useStyles()
  const [atLeast8Chars, setAtLeast8Chars] = useState<boolean>(false)
  const [atLeast1NumberAndSymbol, setAtLeastOneNumberAndSymbol] =
    useState<boolean>(false)
  const [oneLowerCaseCharacter, setOneLowerCaseCharacter] =
    useState<boolean>(false)
  const [oneUpperCaseCharacter, setOneUpperCaseCharacter] =
    useState<boolean>(false)
  const [passwordsMatch, setPasswordsMatch] = useState<boolean>(false)

  useEffect(() => {
    setValidPassword(
      atLeast8Chars &&
        atLeast1NumberAndSymbol &&
        oneLowerCaseCharacter &&
        oneUpperCaseCharacter &&
        passwordsMatch
    )
  }, [
    atLeast1NumberAndSymbol,
    atLeast8Chars,
    passwordsMatch,
    setValidPassword,
    oneLowerCaseCharacter,
    oneUpperCaseCharacter,
  ])

  useEffect(() => {
    const charactersSchema = new PasswordValidator()
    setAtLeast8Chars(
      compare
        ? charactersSchema.is().min(8).validate(comparisonPassword.trim()) ===
            true
        : charactersSchema.is().min(8).validate(password.trim()) === true
    )
    const numberSchema = new PasswordValidator()
    const symbolSchema = new PasswordValidator()
    setAtLeastOneNumberAndSymbol(
      compare
        ? numberSchema.has().digits(1).validate(comparisonPassword.trim()) ===
            true &&
            symbolSchema
              .has()
              .symbols(1)
              .validate(comparisonPassword.trim()) === true
        : numberSchema.has().digits(1).validate(password.trim()) === true &&
            symbolSchema.has().symbols(1).validate(password.trim()) === true
    )
    setPasswordsMatch(
      password !== "" &&
        comparisonPassword !== "" &&
        password.trim() === comparisonPassword.trim()
    )
    const lowerCaseSchema = new PasswordValidator()
    setOneLowerCaseCharacter(
      compare
        ? lowerCaseSchema
            .has()
            .lowercase(1)
            .validate(comparisonPassword.trim()) === true
        : lowerCaseSchema.has().lowercase(1).validate(password.trim()) === true
    )
    const upperCaseSchema = new PasswordValidator()
    setOneUpperCaseCharacter(
      compare
        ? upperCaseSchema
            .has()
            .uppercase(1)
            .validate(comparisonPassword.trim()) === true
        : upperCaseSchema.has().uppercase(1).validate(password.trim()) === true
    )
  }, [comparisonPassword, password])

  return (
    <div className={classes.container} style={{ left }}>
      <div className={classes.passwordRules}>
        <Typography
          variant="body1"
          className={clsx(classes.title, classes.marginBottom12)}
        >
          Password Requirements
        </Typography>
        <div className={clsx(classes.checkContainer, classes.marginBottom12)}>
          <div className={classes.check}>
            {atLeast8Chars ? (
              <CheckIcon />
            ) : (
              <div className={classes.checkSpacer} />
            )}
          </div>
          <Typography variant="body1" className={classes.rule}>
            At least 8 characters
          </Typography>
        </div>
        <div className={clsx(classes.checkContainer, classes.marginBottom12)}>
          <div className={classes.check}>
            {oneLowerCaseCharacter ? (
              <CheckIcon />
            ) : (
              <div className={classes.checkSpacer} />
            )}
          </div>
          <Typography variant="body1" className={classes.rule}>
            At least 1 lowercase letter
          </Typography>
        </div>
        <div className={clsx(classes.checkContainer, classes.marginBottom12)}>
          <div className={classes.check}>
            {oneUpperCaseCharacter ? (
              <CheckIcon />
            ) : (
              <div className={classes.checkSpacer} />
            )}
          </div>
          <Typography variant="body1" className={classes.rule}>
            At least 1 uppercase letter
          </Typography>
        </div>
        <div className={clsx(classes.checkContainer, classes.marginBottom12)}>
          <div className={classes.check}>
            {atLeast1NumberAndSymbol ? (
              <CheckIcon />
            ) : (
              <div className={classes.checkSpacer} />
            )}
          </div>
          <Typography variant="body1" className={classes.rule}>
            At least 1 number and symbol
          </Typography>
        </div>
        <div className={classes.checkContainer}>
          <div className={classes.check}>
            {passwordsMatch ? (
              <CheckIcon />
            ) : (
              <div className={classes.checkSpacer} />
            )}
          </div>
          <Typography variant="body1" className={classes.rule}>
            Passwords match
          </Typography>
        </div>
      </div>
      <div className={classes.triangle} />
    </div>
  )
}

export default PasswordRules
